import type {
  CodingDetails,
  ObservationValueKind,
  ScaleType,
} from '@seen/core/codings'

const SCALE_NAMES: Record<ScaleType, string> = {
  Qn: 'Number',
  Ord: 'Choice',
  Nom: 'Choice',
  Nar: 'Text',
  Ref: 'Reference',
}

const VALUE_KIND_NAMES: Record<ObservationValueKind, string> = {
  date: 'Date',
  dateTime: 'Date & time',
}

// How many answers are spelled out before the rest are counted.
const ANSWERS_SHOWN = 4

// How a code is answered, in one line: "Choice · Continent · Incontinent",
// "Number in [degF]", "Reference · Practitioner", "Date", "Parts · Sys/Dia".
export function observationDetailsSummary(details: CodingDetails): string {
  const answers = details.answerOptions
  const shown = answers.slice(0, ANSWERS_SHOWN).map((answer) => answer.display)
  const more = answers.length - shown.length
  return [
    kindName(details),
    details.referenceResourceType ? ` · ${details.referenceResourceType}` : '',
    details.unit ? ` in ${details.unit}` : '',
    shown.length > 0 ? ` · ${shown.join(' · ')}` : '',
    more > 0 ? ` · ${more} more` : '',
    details.answerValueSet ? ' · answers searched from a value set' : '',
  ].join('')
}

// What kind of value the code takes: a date, its parts, or its scale.
function kindName(details: CodingDetails): string {
  if (details.components?.length) {
    const required = details.components.filter((part) => part.required)
    const optional = details.components.length - required.length
    return [
      `Parts · ${required.map((part) => part.label).join('/')}`,
      ...(optional > 0 ? [`${optional} optional`] : []),
    ].join(' · ')
  }
  return details.valueKind
    ? VALUE_KIND_NAMES[details.valueKind]
    : SCALE_NAMES[details.scaleType]
}
